import {
  cn,
  Loader,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
} from "@devhop/ui";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { getCookie } from "@/shared/lib/cookies";
import { SkipToMain } from "@/shared/ui/SkipToMain";
import { LayoutProvider } from "../providers/LayoutProvider";
import { AppSidebar } from "./AppSidebar";
import { sidebarData } from "./data/sidebar-data";
import { NavGroup } from "./NavGroup";
import { NavUser } from "./NavUser";
import { SidebarBrand } from "./SidebarBrand";
import {
  SidebarNavAlertsProvider,
  useSidebarNavGroupsWithBadges,
} from "./SidebarNavAlertsContext";

type AuthenticatedLayoutProps = {
  children?: React.ReactNode;
};

function AuthenticatedAppChrome({ children }: { children?: ReactNode }) {
  const navGroups = useSidebarNavGroupsWithBadges();
  return (
    <>
      <SkipToMain />
      <AppSidebar>
        <SidebarHeader>
          <SidebarBrand />
        </SidebarHeader>
        <SidebarContent>
          {navGroups.map((props) => (
            <NavGroup key={props.title} {...props} />
          ))}
        </SidebarContent>
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
        <SidebarRail />
      </AppSidebar>
      <SidebarInset
        className={cn(
          "has-[[data-layout=fixed]]:h-svh",
          "peer-data-[variant=inset]:has-[[data-layout=fixed]]:h-[calc(100svh-(var(--spacing)*4))]",
          "@container/content",
        )}
      >
        {children ?? <Outlet />}
      </SidebarInset>
    </>
  );
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie("sidebar_state") !== "false";
  const { isLoading, isAuthenticated } = useAuthState();
  const navigate = useNavigate({ from: "/app" });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/auth/login" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Loader />
      </div>
    );
  }
  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <LayoutProvider>
        <SidebarNavAlertsProvider
          enabled={isAuthenticated}
          baseNavGroups={sidebarData.navGroups}
        >
          <AuthenticatedAppChrome>{children}</AuthenticatedAppChrome>
        </SidebarNavAlertsProvider>
      </LayoutProvider>
    </SidebarProvider>
  );
}
